import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { useExternalInvitationsForm } from '@/hooks/shared/useExternalInvitationsForm';
import type { RootState, AppDispatch } from '@/store';

interface NewExternalUser {
  name: string;
  email: string;
  phoneNumber: string;
  department?: string;
}

interface ExternalUserWithDeptOrg {
  id: string;
  name: string;
  email: string;
  deptOrg: string;
}

interface UserWithEmail {
  email: string;
}

interface DraftWithExternalFields {
  externalParticipantIds: string[];
  newExternalUsers: NewExternalUser[];
}

interface ExternalInvitationsAdapterConfig<Draft extends DraftWithExternalFields> {
  selectDraft: (state: RootState) => Draft;
  selectInternalUsers: (state: RootState) => UserWithEmail[];
  selectExternalUsers: (state: RootState) => ExternalUserWithDeptOrg[];
  selectExternalSeatsAvailable: (state: RootState) => number;
  dispatchSetExternalParticipantIds: (dispatch: AppDispatch, value: string[]) => void;
  dispatchSetNewExternalUsers: (dispatch: AppDispatch, value: NewExternalUser[]) => void;
  dispatchSetHasPendingEntries: (dispatch: AppDispatch, value: boolean) => void;
}

export function useExternalInvitationsAdapter<Draft extends DraftWithExternalFields>(
  config: ExternalInvitationsAdapterConfig<Draft>
) {
  const {
    selectDraft,
    selectInternalUsers,
    selectExternalUsers,
    selectExternalSeatsAvailable,
    dispatchSetExternalParticipantIds,
    dispatchSetNewExternalUsers,
    dispatchSetHasPendingEntries,
  } = config;

  const dispatch = useAppDispatch();
  const draftState = useAppSelector(selectDraft);
  const internalUsers = useAppSelector(selectInternalUsers);
  const externalUsers = useAppSelector(selectExternalUsers);
  const externalSeatsAvailable = useAppSelector(selectExternalSeatsAvailable);
  const existingUserEmails = [...internalUsers, ...externalUsers].map((user) => user.email);

  const handleAddContact = useCallback(
    (contactId: string): void => {
      dispatchSetExternalParticipantIds(
        dispatch,
        Array.from(new Set([...draftState.externalParticipantIds, contactId]))
      );
    },
    [dispatch, dispatchSetExternalParticipantIds, draftState.externalParticipantIds]
  );

  const handleRemoveContact = useCallback(
    (contactId: string): void => {
      dispatchSetExternalParticipantIds(
        dispatch,
        draftState.externalParticipantIds.filter((id) => id !== contactId)
      );
    },
    [dispatch, dispatchSetExternalParticipantIds, draftState.externalParticipantIds]
  );

  const handleAddNewExternalUser = useCallback(
    (user: NewExternalUser): void => {
      dispatchSetNewExternalUsers(dispatch, [...draftState.newExternalUsers, user]);
    },
    [dispatch, dispatchSetNewExternalUsers, draftState.newExternalUsers]
  );

  const handleRemoveNewExternalUser = useCallback(
    (userIndex: number): void => {
      dispatchSetNewExternalUsers(
        dispatch,
        draftState.newExternalUsers.filter((_, index) => index !== userIndex)
      );
    },
    [dispatch, dispatchSetNewExternalUsers, draftState.newExternalUsers]
  );

  const handlePendingManualEntriesChange = useCallback(
    (hasPendingEntries: boolean): void => {
      dispatchSetHasPendingEntries(dispatch, hasPendingEntries);
    },
    [dispatch, dispatchSetHasPendingEntries]
  );

  return useExternalInvitationsForm({
    externalUsers,
    existingUserEmails,
    externalSeatsAvailable,
    disabledContactIds: draftState.externalParticipantIds,
    newExternalUsers: draftState.newExternalUsers,
    onAddContact: handleAddContact,
    onRemoveContact: handleRemoveContact,
    onAddNewExternalUser: handleAddNewExternalUser,
    onRemoveNewExternalUser: handleRemoveNewExternalUser,
    onPendingManualEntriesChange: handlePendingManualEntriesChange,
  });
}
